import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Save, X, CheckCircle2, Star, Ban, Plane, Stethoscope, ChevronLeft, ChevronRight, Sun, CloudSun, Moon, MoonStar, Bed, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isToday,
} from 'date-fns';

// 0=Sun..6=Sat per backend convention.
const DAYS = [
  { dow: 1, short: 'Mon' },
  { dow: 2, short: 'Tue' },
  { dow: 3, short: 'Wed' },
  { dow: 4, short: 'Thu' },
  { dow: 5, short: 'Fri' },
  { dow: 6, short: 'Sat' },
  { dow: 0, short: 'Sun' },
];

const EXC_TYPES = [
  { id: 'UNAVAILABLE',        label: 'Unavailable',     Icon: Ban,         cls: 'bg-red-100 text-red-800 border-red-300' },
  { id: 'LEAVE_SICK',         label: 'Sick Leave',      Icon: Stethoscope, cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  { id: 'LEAVE_ANNUAL',       label: 'Annual Leave',    Icon: Plane,       cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  { id: 'PREFERRED',          label: 'Preferred',       Icon: Star,        cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { id: 'AVAILABLE_OVERRIDE', label: 'Available',       Icon: CheckCircle2,cls: 'bg-blue-100 text-blue-800 border-blue-300' },
];

// NDIS-aligned shift presets — deliberately matches the Roster shift_period taxonomy.
// "ACTIVE_NIGHT" and "SLEEPOVER" share 22:00–06:00 but only the latter sets is_sleepover.
const SHIFT_PRESETS = [
  { id: 'MORNING',     label: 'Morning',      start: '06:00', end: '14:00', Icon: Sun,      cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  { id: 'AFTERNOON',   label: 'Afternoon',    start: '14:00', end: '18:00', Icon: CloudSun, cls: 'bg-orange-50 text-orange-800 border-orange-300' },
  { id: 'EVENING',     label: 'Evening',      start: '18:00', end: '22:00', Icon: Moon,     cls: 'bg-indigo-50 text-indigo-800 border-indigo-300' },
  { id: 'NIGHT',       label: 'Active Night', start: '22:00', end: '06:00', Icon: MoonStar, cls: 'bg-slate-100 text-slate-800 border-slate-300' },
  { id: 'SLEEPOVER',   label: 'Sleepover',    start: '22:00', end: '06:00', Icon: Bed,      cls: 'bg-violet-50 text-violet-800 border-violet-300' },
  { id: 'CUSTOM',      label: 'Custom',       start: null,    end: null,    Icon: Settings2,cls: 'bg-slate-50 text-slate-700 border-slate-200' },
];

function presetById(id) {
  return SHIFT_PRESETS.find(p => p.id === id);
}

function excTypeMeta(id) {
  return EXC_TYPES.find(t => t.id === id) || EXC_TYPES[0];
}

function ExceptionPill({ type }) {
  const meta = excTypeMeta(type);
  const Icon = meta.Icon;
  return (
    <span className={'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ' + meta.cls}>
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
}

// ── Time-merging helper ─────────────────────────────────────────────────
// When multiple presets are selected we union their time ranges. Treat
// times as minutes-of-day so cross-midnight (NIGHT/SLEEPOVER 22:00–06:00)
// merges sensibly with adjacent ranges.
function toMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
}
function fromMin(min) {
  const h = Math.floor((min + 1440) % 1440 / 60);
  const m = (min + 1440) % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

function mergePresetTimes(types) {
  // Filter out CUSTOM and unknowns.
  const ts = types.map(presetById).filter(p => p && p.start);
  if (ts.length === 0) return null;
  if (ts.length === 1) return { start_time: ts[0].start, end_time: ts[0].end };

  // Has any wrap-around (end < start)?
  const anyWrap = ts.some(p => toMin(p.end) <= toMin(p.start));
  if (anyWrap) {
    // For mixed wrap + non-wrap presets, fall back to earliest start + latest end (modulo 24h).
    const earliestStart = ts.reduce((a, p) => Math.min(a, toMin(p.start)), 24 * 60);
    const latestEnd = ts.reduce((a, p) => Math.max(a, toMin(p.end)), 0);
    return { start_time: fromMin(earliestStart), end_time: fromMin(latestEnd) };
  }
  // All within same day — straightforward min/max.
  const earliestStart = ts.reduce((a, p) => Math.min(a, toMin(p.start)), 24 * 60);
  const latestEnd = ts.reduce((a, p) => Math.max(a, toMin(p.end)), 0);
  return { start_time: fromMin(earliestStart), end_time: fromMin(latestEnd) };
}

// Best-effort: detect which preset(s) the current start/end matches exactly.
function detectPresets(start, end) {
  const matches = SHIFT_PRESETS
    .filter(p => p.start && p.start === start && p.end === end)
    .map(p => p.id);
  return matches;
}


function PresetChip({ preset, active, onToggle, disabled }) {
  const Icon = preset.Icon;
  const baseCls = active
    ? 'ring-2 ring-offset-1 ring-slate-900 ' + preset.cls
    : preset.cls + ' opacity-70 hover:opacity-100';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggle(preset.id)}
      className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium transition ' + baseCls}
      data-testid={'shift-preset-chip-' + preset.id.toLowerCase()}
      title={preset.start ? `${preset.start}–${preset.end}` : 'Custom time'}
    >
      <Icon className="w-2.5 h-2.5" />
      {preset.label}
    </button>
  );
}


function AvailabilityMonthGrid({ date, weekly, exceptions }) {
  const grid = useMemo(() => {
    const ms = startOfMonth(date);
    const me = endOfMonth(date);
    const gs = startOfWeek(ms, { weekStartsOn: 1 });
    const ge = endOfWeek(me, { weekStartsOn: 1 });
    const days = [];
    let d = gs;
    while (d <= ge) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [date]);

  const excMap = useMemo(() => {
    const m = {};
    (exceptions || []).forEach(e => { if (e.date) m[e.date] = e; });
    return m;
  }, [exceptions]);

  const weeklyByDow = useMemo(() => {
    const m = {};
    (weekly || []).forEach(w => { m[w.day_of_week] = w; });
    return m;
  }, [weekly]);

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map(d => (
          <div key={d.short} className="text-[10px] uppercase tracking-wider text-muted-foreground text-center font-semibold">{d.short}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map(d => {
          const dk = format(d, 'yyyy-MM-dd');
          const exc = excMap[dk];
          const dow = d.getDay(); // 0=Sun..6=Sat
          const wk = weeklyByDow[dow];
          const inMonth = isSameMonth(d, date);
          const today = isToday(d);

          let cellCls = 'bg-white border-slate-200';
          let badgeContent = null;

          if (exc) {
            const meta = excTypeMeta(exc.type);
            cellCls = meta.cls + ' border';
            badgeContent = (
              <div className="text-[9px] font-medium truncate">{meta.label}</div>
            );
          } else if (wk && (wk.ranges || []).length > 0) {
            cellCls = 'bg-emerald-50 border-emerald-200';
            const r = wk.ranges[0];
            const types = r.types || [];
            // Show readable preset labels when one or two are set, fall back to time range.
            let label = `${r.start_time}–${r.end_time}`;
            if (types.length >= 1 && types.length <= 2 && !types.includes('CUSTOM')) {
              label = types.map(t => presetById(t)?.label || t).join(' + ');
            }
            badgeContent = (
              <div className="text-[9px] tabular-nums text-emerald-800 truncate" title={`${r.start_time}–${r.end_time}`}>
                {label}
              </div>
            );
          } else {
            cellCls = 'bg-slate-50 border-slate-200';
          }

          const dimCls = inMonth ? '' : 'opacity-40';
          const todayCls = today ? 'ring-2 ring-blue-500' : '';

          return (
            <div key={dk} className={'rounded-md border p-1 min-h-[58px] ' + cellCls + ' ' + dimCls + ' ' + todayCls}>
              <div className="text-[11px] font-semibold tabular-nums leading-none">{format(d, 'd')}</div>
              <div className="mt-1">{badgeContent}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function PortalAvailabilityPage() {
  const [weekly, setWeekly] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [newExc, setNewExc] = useState({ date: '', type: 'UNAVAILABLE', notes: '' });

  useEffect(() => {
    api.get('/availability/my').then(r => {
      setWeekly(r.data.data?.weekly || []);
      setExceptions(r.data.data?.exceptions || []);
    }).catch(() => {});
    api.get('/production/feature-flags/mine').then(r => {
      const f = r.data?.data || r.data || {};
      setEnabled(f.staff_availability_enabled !== false);
    }).catch(() => {});
  }, []);

  // Helpers — operate on the current weekly[] state.
  const getDayEntry = (dow) => weekly.find(w => w.day_of_week === dow);

  const setDayEntry = (dow, partial) => {
    setWeekly(prev => prev.map(w => w.day_of_week === dow ? { ...w, ...partial } : w));
  };

  const removeDayEntry = (dow) => {
    setWeekly(prev => prev.filter(w => w.day_of_week !== dow));
  };

  const ensureDayEntry = (dow, defaults) => {
    const existing = getDayEntry(dow);
    if (existing) return existing;
    const fresh = { day_of_week: dow, ranges: [{ start_time: '08:00', end_time: '17:00', types: [], is_sleepover: false }], ...defaults };
    setWeekly(prev => [...prev, fresh]);
    return fresh;
  };

  const toggleDayCheckbox = (dow) => {
    if (getDayEntry(dow)) removeDayEntry(dow);
    else ensureDayEntry(dow);
  };

  const togglePreset = (dow, presetId) => {
    const existing = getDayEntry(dow);
    if (presetId === 'CUSTOM') {
      // CUSTOM is a state, not a preset to merge with others — clear other types,
      // keep current times so user can edit freely.
      if (!existing) {
        setWeekly(prev => [...prev, {
          day_of_week: dow,
          ranges: [{ start_time: '08:00', end_time: '17:00', types: ['CUSTOM'], is_sleepover: false }],
        }]);
      } else {
        setDayEntry(dow, {
          ranges: [{ ...(existing.ranges?.[0] || {}), types: ['CUSTOM'], is_sleepover: false }],
        });
      }
      return;
    }

    // Toggle preset in the day's first range.types[]
    const baseRange = existing?.ranges?.[0] || { start_time: '08:00', end_time: '17:00', types: [], is_sleepover: false };
    const cur = (baseRange.types || []).filter(t => t !== 'CUSTOM');
    const has = cur.includes(presetId);
    const nextTypes = has ? cur.filter(t => t !== presetId) : [...cur, presetId];

    if (nextTypes.length === 0) {
      // No presets selected — degrade to CUSTOM with previous times preserved.
      if (existing) {
        setDayEntry(dow, { ranges: [{ ...baseRange, types: ['CUSTOM'], is_sleepover: false }] });
      } else {
        removeDayEntry(dow);
      }
      return;
    }

    const merged = mergePresetTimes(nextTypes);
    const isSleepover = nextTypes.includes('SLEEPOVER');
    const nextRange = {
      start_time: merged?.start_time || baseRange.start_time,
      end_time: merged?.end_time || baseRange.end_time,
      types: nextTypes,
      is_sleepover: isSleepover,
    };
    if (existing) setDayEntry(dow, { ranges: [nextRange] });
    else setWeekly(prev => [...prev, { day_of_week: dow, ranges: [nextRange] }]);
  };

  const updateRangeTime = (dow, field, value) => {
    setWeekly(prev => prev.map(w => {
      if (w.day_of_week !== dow) return w;
      const r = w.ranges?.[0] || { start_time: '08:00', end_time: '17:00', types: [], is_sleepover: false };
      // Manual time edit → switch to CUSTOM, drop preset types.
      const next = { ...r, [field]: value, types: ['CUSTOM'], is_sleepover: false };
      return { ...w, ranges: [next] };
    }));
  };

  // ── Exceptions handlers (unchanged) ──
  const addException = () => {
    if (!newExc.date) return;
    if (exceptions.some(e => e.date === newExc.date)) {
      toast.error('An exception already exists for this date');
      return;
    }
    setExceptions([...exceptions, { ...newExc, ranges: [] }]);
    setNewExc({ date: '', type: 'UNAVAILABLE', notes: '' });
  };

  const removeException = (i) => setExceptions(exceptions.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/availability/my', { weekly, exceptions });
      toast.success('Availability saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!enabled) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">My Availability</h1>
        <Card><CardContent className="p-6 text-sm text-muted-foreground">
          The availability feature is currently disabled. Contact your administrator to enable <code>staff_availability_enabled</code>.
        </CardContent></Card>
      </div>
    );
  }

  const monthLabel = format(viewMonth, 'MMMM yyyy');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="portal-availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">My Availability</h1>
          <p className="text-muted-foreground mt-1">Set your weekly schedule, mark leave or one-off changes.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="availability-save-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekly schedule */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Weekly Schedule</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {DAYS.map(({ dow, short }) => {
              const entry = getDayEntry(dow);
              const r = entry?.ranges?.[0] || {};
              const activeTypes = r.types || (entry ? detectPresets(r.start_time, r.end_time) : []);
              const isCustom = activeTypes.includes('CUSTOM') || (entry && !activeTypes.length);
              return (
                <div key={dow} className="space-y-1.5" data-testid={'availability-day-row-' + short.toLowerCase()}>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-24 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!entry}
                        onChange={() => toggleDayCheckbox(dow)}
                        className="rounded"
                        data-testid={'availability-toggle-' + short.toLowerCase()}
                      />
                      <span className="text-sm font-medium">{short}</span>
                    </label>
                    {entry ? (
                      <div className="flex items-center gap-2">
                        <TrinitiTimePicker
                          className="w-28 h-8 text-sm"
                          value={r.start_time || '08:00'}
                          onChange={(v) => updateRangeTime(dow, 'start_time', v)}
                          testId={'availability-start-time-' + short.toLowerCase()}
                        />
                        <span className="text-muted-foreground text-xs">to</span>
                        <TrinitiTimePicker
                          className="w-28 h-8 text-sm"
                          value={r.end_time || '17:00'}
                          onChange={(v) => updateRangeTime(dow, 'end_time', v)}
                          testId={'availability-end-time-' + short.toLowerCase()}
                        />
                        {r.is_sleepover && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-300">
                            <Bed className="w-2.5 h-2.5" /> Sleepover
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unavailable</span>
                    )}
                  </div>
                  {entry && (
                    <div className="flex flex-wrap gap-1.5 pl-[6.5rem]" data-testid={'availability-presets-' + short.toLowerCase()}>
                      {SHIFT_PRESETS.map(p => (
                        <PresetChip
                          key={p.id}
                          preset={p}
                          active={p.id === 'CUSTOM' ? isCustom : activeTypes.includes(p.id)}
                          onToggle={(id) => togglePreset(dow, id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-muted-foreground pt-1">
              Tip: pick one or more presets to auto-fill times. Adjusting the time fields manually switches the day to <strong>Custom</strong>.
            </p>
          </CardContent>
        </Card>

        {/* Calendar preview */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Calendar Preview</CardTitle>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setViewMonth(d => subMonths(d, 1))} data-testid="availability-month-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
              <span className="text-xs font-medium tabular-nums w-28 text-center">{monthLabel}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setViewMonth(d => addMonths(d, 1))} data-testid="availability-month-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <AvailabilityMonthGrid date={viewMonth} weekly={weekly} exceptions={exceptions} />
            <div className="flex flex-wrap gap-2 pt-3 text-[10px] text-muted-foreground">
              <span className="inline-block w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> Available
              {EXC_TYPES.map(t => (
                <span key={t.id} className="inline-flex items-center gap-1">
                  <span className={'inline-block w-3 h-3 rounded border ' + t.cls} />
                  {t.label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exceptions list */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Exceptions &amp; Leave</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {exceptions.length === 0 ? (
            <div className="text-xs text-muted-foreground py-2">No exceptions yet. Use the form below to mark a date.</div>
          ) : (
            exceptions
              .slice()
              .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
              .map((exc, i) => {
                const realIdx = exceptions.findIndex(e => e.date === exc.date && e.type === exc.type);
                return (
                  <div key={exc.date + '-' + i} className="flex items-center gap-3 text-sm border rounded-md p-2 bg-white" data-testid={'availability-exc-row-' + exc.date}>
                    <span className="font-mono tabular-nums w-24">{exc.date}</span>
                    <ExceptionPill type={exc.type} />
                    <span className="text-muted-foreground flex-1 truncate">{exc.notes}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeException(realIdx)} data-testid={'availability-exc-remove-' + exc.date}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })
          )}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <TrinitiDatePicker
              className="w-44 h-8"
              value={newExc.date}
              onChange={(v) => setNewExc({ ...newExc, date: v })}
              testId="availability-new-exc-date"
            />
            <Select value={newExc.type} onValueChange={(v) => setNewExc({ ...newExc, type: v })}>
              <SelectTrigger className="w-[180px] h-8" data-testid="availability-new-exc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXC_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              className="h-8 flex-1 min-w-[180px]"
              placeholder="Notes (optional)"
              value={newExc.notes}
              onChange={(e) => setNewExc({ ...newExc, notes: e.target.value })}
              data-testid="availability-new-exc-notes"
            />
            <Button size="sm" onClick={addException} disabled={!newExc.date} data-testid="availability-new-exc-add">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground pt-1">
            Heads-up: availability is informational for now — it will not block scheduling. Admins can see what you set.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
